/* @flow */
import idx from 'idx';
import * as React from 'react';
import { graphql } from 'react-apollo';

import AsyncSelect from 'components/AsyncSelect';
import AsyncSelectItem from 'components/AsyncSelectItem';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import DefaultArticles from 'support/constants/DefaultArticles.json';
import Dialog from 'components/Dialog';
import FaqActions from 'actions/FaqActions';
import Feature from 'components/Feature';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import SUPPORT_DIALOG_QUERY from 'support/queries/SupportDialogQuery.gql';
import Text from 'components/Text';
import SupportChat from 'support/components/SupportChat';

import type { SupportDialog as SupportDialogQuery } from 'support/queries/__generated__/SupportDialog';

import './styles/SupportDialog.scss';

type Props = {
  data: ReactApolloData<SupportDialogQuery>,
  onRequestHide?: () => any,
};

class SupportDialog extends React.Component<Props> {
  handleResultSelected = (item: Object) => {
    if (!item.props || !item.props.url) {
      return;
    }

    const link = item.props.url;
    window.open(link, '_blank');
  };

  highlightMatches = (text: string, searchString: string) => {
    const lowerText = text.toLowerCase();
    const searchWords = searchString.split(' ').filter(word => word.length >= 3);

    const matches = [];

    searchWords.forEach(word => {
      const index = lowerText.indexOf(word.toLowerCase());
      if (index >= 0) {
        matches.push([index, word]);
      }
    });

    if (matches.length > 0) {
      matches.sort((a, b) => {
        if (a[0] < b[0]) {
          return -1;
        } else if (b[0] < a[0]) {
          return 1;
        } else {
          if (a[1].length > b[1].length) {
            return -1;
          } else {
            return 1;
          }
        }
      });

      const match = matches[0];

      const list = [
        <span>{text.slice(0, match[0])}</span>,
        <span className="highlighted">{match[1]}</span>,
      ];

      return list.concat(
        this.highlightMatches(text.slice(match[0] + match[1].length), searchString)
      );
    }

    return [<span>{text}</span>];
  };

  searchAnswers = (query: string, callback: Function) => {
    if (query.length === 0) {
      return;
    }

    callback(
      FaqActions.loadFaqs(query).then(faqs => {
        return faqs.slice(0, 4).map(article => this.renderSearchResult(article, query));
      })
    );
  };

  renderNoResults = () => {
    const body = (
      <div>
        <Link>Contact us</Link> with your question
      </div>
    );
    const resultTemplate = this.renderSearchResultTemplate(
      LinkConstants.CONTACT_FORM,
      'We don’t have any FAQs about this topic yet.',
      body,
      'SupportDialog-faqSearchResults-item--noResult'
    );
    return <div className="SupportDialog-noResults">{resultTemplate}</div>;
  };

  renderDefaultArticles = () => {
    return DefaultArticles.map((article, index: number) => {
      return this.renderSearchResultTemplate(article.url, article.title, article.body, '', index);
    });
  };

  renderSearchResult = (article: Object, searchString: string) => {
    const e = document.createElement('html');
    e.innerHTML = article.getBody();
    let text = e.innerText || e.textContent; // trim off extra spaces/NBSPs from the text
    text = text.replace(/^[ \u00a0]*/, '');
    return this.renderSearchResultTemplate(
      article.getUrl(),
      this.highlightMatches(article.getTitle(), searchString),
      this.highlightMatches(text, searchString),
      '',
      article.id
    );
  };

  renderSearchResultTemplate = (
    url: string,
    name: any,
    body: any,
    className: string = '',
    id: number = -1
  ) => {
    return (
      <AsyncSelectItem id={id} title="" url={url}>
        <Link
          href={url}
          target="_blank"
          className={`SupportDialog-faqSearchResults-item ${className}`}>
          <h3 className="SupportDialog-faqSearchResults-item-title">{name}</h3>
          <Text truncate className="SupportDialog-faqSearchResults-item-body">
            {body}
          </Text>
        </Link>
      </AsyncSelectItem>
    );
  };

  renderFAQQuickLinks = () => {
    const links = [
      {
        label: 'Unread notifications',
        url: LinkConstants.UNREAD_NOTIFICATIONS,
      },
      {
        label: 'Leave a class',
        url: LinkConstants.LEAVE_CLASS,
      },
      {
        label: 'Merge accounts',
        url: LinkConstants.MERGE_ACCOUNTS,
      },
    ];
    return (
      <Buttons align="left">
        {links.map(link => (
          <Button href={link.url} target="_blank" color="paper" size="xxxsmall" weight="normal">
            <Text size="xxsmall">{link.label}</Text>
          </Button>
        ))}
      </Buttons>
    );
  };

  renderSupportLink = (isAdminOfPaidOrg: boolean, loading: boolean) => {
    if (loading) {
      return null;
    }

    const defaultEmailSupportLink = (
      <Text size="xxsmall">
        You can also{' '}
        <Link
          href={LinkConstants.CONTACT_FORM}
          target="_blank"
          eventElementName="SupportDialog-actions-link">
          <Text weight="semibold">email our support team</Text>
        </Link>
      </Text>
    );
    const emailAndPhoneSupportLinks = (
      <Text size="xxsmall">
        <Link
          href={LinkConstants.CONTACT_FORM}
          target="_blank"
          eventElementName="SupportDialog-actions-link">
          <Text weight="semibold">Email support</Text>
        </Link>{' '}
        •{' '}
        <Link
          onClick={() =>
            window.open(
              LinkConstants.SUPPORT_CALL,
              'targetWindow',
              'toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=500,height=350'
            )
          }>
          <Text weight="semibold">Click here</Text>
        </Link>{' '}
        to call us (weekdays 7:30am–1:30pm PST)
      </Text>
    );

    return (
      <Feature
        name="support_calls.web.feb-20"
        on={isAdminOfPaidOrg ? emailAndPhoneSupportLinks : defaultEmailSupportLink}
        off={defaultEmailSupportLink}
      />
    );
  };

  render = () => {
    const { data, onRequestHide } = this.props;
    const { me, loading } = data;
    const isAdminOfPaidOrg = !!idx(me, _ => _.organizations.length);

    return (
      <Dialog
        image={require('assets/images/help/hot-air-balloon.svg')}
        imageSize="large"
        serif
        title="How can we help?"
        size="small"
        headerSize="small"
        className="SupportDialog"
        eventScreenName="SupportDialog"
        onRequestHide={onRequestHide}>
        <BoxBody size="small">
          <Text align="left">
            <Row size="xxxlarge">
              <Row size="xsmall">
                <AsyncSelect
                  layerClassName="SupportDialog-faqSearch"
                  searchCallback={this.searchAnswers}
                  onSelect={this.handleResultSelected}
                  initialItems={this.renderDefaultArticles()}
                  noResults={this.renderNoResults()}
                  placeholder="Search help center"
                  clearOnInputChange
                  inputDelay={500}
                  noQuery=" "
                />
              </Row>
              {this.renderFAQQuickLinks()}
            </Row>
            <Row>
              <SupportChat liveAgent={isAdminOfPaidOrg} />
            </Row>
            {this.renderSupportLink(isAdminOfPaidOrg, loading)}
          </Text>
        </BoxBody>
      </Dialog>
    );
  };
}

const withMe = graphql(SUPPORT_DIALOG_QUERY);

export default withMe(SupportDialog);
